import React, { useState, useEffect, useCallback } from 'react';

export default function GalleryModal({ images, currentIndex, isOpen, onClose, onNavigate }) {
    const [animating, setAnimating] = useState(false);
    const [direction, setDirection] = useState(null);

    const handlePrev = useCallback(() => {
        if (animating) return;
        setDirection('left');
        setAnimating(true);
        setTimeout(() => {
            onNavigate((currentIndex - 1 + images.length) % images.length);
            setAnimating(false);
        }, 250);
    }, [animating, currentIndex, images.length, onNavigate]);

    const handleNext = useCallback(() => {
        if (animating) return;
        setDirection('right');
        setAnimating(true);
        setTimeout(() => {
            onNavigate((currentIndex + 1) % images.length);
            setAnimating(false);
        }, 250);
    }, [animating, currentIndex, images.length, onNavigate]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isOpen) return;
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft') handlePrev();
            if (e.key === 'ArrowRight') handleNext();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose, handlePrev, handleNext]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    if (!isOpen || !images.length) return null;

    const current = images[currentIndex];

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center modal-backdrop"
            onClick={onClose}
        >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />

            {/* Close button */}
            <button
                onClick={onClose}
                className="absolute top-4 right-4 md:top-6 md:right-6 z-50 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-all duration-300 hover:rotate-90"
                aria-label="Close"
            >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            {/* Counter */}
            <div className="absolute top-4 left-4 md:top-6 md:left-6 z-50 text-white/70 font-sans text-sm tracking-wider">
                {currentIndex + 1} / {images.length}
            </div>

            {/* Previous button */}
            <button
                onClick={(e) => { e.stopPropagation(); handlePrev(); }}
                className="absolute left-2 md:left-6 z-50 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-white/10 hover:bg-gold-500/30 border border-white/20 hover:border-gold-400/40 text-white transition-all duration-300 group"
                aria-label="Previous"
            >
                <svg className="w-6 h-6 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            {/* Next button */}
            <button
                onClick={(e) => { e.stopPropagation(); handleNext(); }}
                className="absolute right-2 md:right-6 z-50 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-white/10 hover:bg-gold-500/30 border border-white/20 hover:border-gold-400/40 text-white transition-all duration-300 group"
                aria-label="Next"
            >
                <svg className="w-6 h-6 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
            </button>

            {/* Image */}
            <div
                className="relative z-10 max-w-[92vw] max-h-[85vh] modal-content"
                onClick={(e) => e.stopPropagation()}
            >
                <img
                    src={current.src}
                    alt={current.alt || 'Gallery image'}
                    className={`max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl transition-all duration-250 ${animating
                            ? direction === 'right'
                                ? 'opacity-0 -translate-x-8'
                                : 'opacity-0 translate-x-8'
                            : 'opacity-100 translate-x-0'
                        }`}
                />
                {current.caption && (
                    <p className="text-center text-white/80 font-body text-lg mt-4 italic">
                        {current.caption}
                    </p>
                )}
            </div>
        </div>
    );
}
